/**
 * TimeTracker — time tracking UI for task detail.
 *
 * Two states:
 * 1. Timer running: prominent live display with stop button
 * 2. Timer stopped: compact total + entries + start/log actions
 *
 * Design: Entries grouped by day with left accent border,
 * inspired by Timery's scannable layout. Running state feels alive.
 */

import { memo, useState, useCallback, useEffect, useRef, useMemo } from "react"
import { useTranslation } from "react-i18next"
import TimerIcon from "@/shared/icons/pikaicons/stroke/time/timer-default.svg?react"
import PlayIcon from "@/shared/icons/pikaicons/solid/media/play-circle.svg?react"
import StopIcon from "@/shared/icons/pikaicons/solid/media/stop-circle.svg?react"
import PlusIcon from "@/shared/icons/pikaicons/stroke/maths/plus-circle.svg?react"
import TrashIcon from "@/shared/icons/pikaicons/stroke/general/delete-dustbin-01.svg?react"
import ChevronIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/chevron-down.svg?react"
import {
  startTimer,
  stopTimer,
  logTime,
  deleteTimeEntry,
  getTimeTrackingInfo,
  formatDuration,
} from "@/db/operations"
import type { TimeEntry } from "@/db/operations"
import type { DbObject } from "@/db/schema"
import dayjs from "dayjs"

interface TimeTrackerProps {
  task: DbObject
}

/**
 * Format ms to live HH:MM:SS (precise, for running timer).
 */
function formatLive(ms: number): string {
  const s = Math.floor(ms / 1000)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
  return `${m}:${String(sec).padStart(2, "0")}`
}

export const TimeTracker = memo(function TimeTracker({ task }: TimeTrackerProps) {
  const { t } = useTranslation()
  const taskId = task.id!
  const info = getTimeTrackingInfo(task)
  const [showLogInput, setShowLogInput] = useState(false)
  const [logHours, setLogHours] = useState(0)
  const [logMinutes, setLogMinutes] = useState(30)
  const [logNote, setLogNote] = useState("")
  const [expanded, setExpanded] = useState(false)

  // Live timer
  const [elapsed, setElapsed] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined)

  useEffect(() => {
    if (info.isRunning && info.timerStartedAt) {
      const startMs = Number(info.timerStartedAt)
      const update = () => setElapsed(Date.now() - startMs)
      update()
      intervalRef.current = setInterval(update, 1000)
      return () => clearInterval(intervalRef.current)
    } else {
      setElapsed(0)
    }
  }, [info.isRunning, info.timerStartedAt])

  const handleStart = useCallback(() => void startTimer(taskId), [taskId])
  const handleStop = useCallback(() => void stopTimer(taskId), [taskId])

  const handleLogTime = useCallback(() => {
    const ms = (logHours * 60 + logMinutes) * 60_000
    if (ms > 0) {
      void logTime(taskId, ms, logNote.trim() || undefined)
      setLogHours(0)
      setLogMinutes(30)
      setLogNote("")
      setShowLogInput(false)
    }
  }, [taskId, logHours, logMinutes, logNote])

  const handleDeleteEntry = useCallback(
    (entryId: string) => void deleteTimeEntry(taskId, entryId),
    [taskId],
  )

  const totalWithRunning = info.totalMs + (info.isRunning ? elapsed : 0)
  const entryCount = info.entries.length

  // Group entries by day (newest day first, newest entry first within day)
  const groupedEntries = useMemo(() => {
    const sorted = [...info.entries].sort(
      (a, b) => Number(b.startedAt) - Number(a.startedAt),
    )
    const groups: { label: string; dayKey: string; entries: TimeEntry[] }[] = []
    const now = dayjs()
    for (const entry of sorted) {
      const d = dayjs(Number(entry.startedAt))
      const dayKey = d.format("YYYY-MM-DD")
      let label: string
      if (d.isSame(now, "day")) label = t("timeTracking.today", "Today")
      else if (d.isSame(now.subtract(1, "day"), "day"))
        label = t("timeTracking.yesterday", "Yesterday")
      else label = d.format("ddd, D MMM")
      const existing = groups.find((g) => g.dayKey === dayKey)
      if (existing) existing.entries.push(entry)
      else groups.push({ label, dayKey, entries: [entry] })
    }
    return groups
  }, [info.entries, t])

  // Entries hidden by default — single-line header with chevron to expand (Notion pattern)
  const EXPANDED_MAX = 5

  // ── Unified layout: header (running or stopped) + entries below ─────
  return (
    <div className="flex flex-col gap-1.5">
      {/* Header row — running or stopped, consistent height */}
      {info.isRunning ? (
        <div className="flex h-8 items-center gap-2">
          {/* Pulsing dot — centered in same space as TimerIcon (size-4) */}
          <span className="flex size-4 items-center justify-center">
            <span className="relative flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-red-500" />
            </span>
          </span>

          {/* Live duration — same text-sm as stopped total for consistency */}
          <span className="text-sm font-semibold tabular-nums tracking-tight text-foreground">
            {formatLive(elapsed)}
          </span>

          {/* Total in parentheses */}
          {info.totalMs > 0 && (
            <span className="text-xs text-muted-foreground/50">
              ({formatDuration(totalWithRunning)} total)
            </span>
          )}

          {/* Expand entries chevron */}
          {entryCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-0.5 rounded px-1 py-0.5 text-xs tabular-nums text-muted-foreground/60 transition-colors hover:bg-accent hover:text-foreground"
            >
              <span>{entryCount}</span>
              <ChevronIcon className={`size-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
          )}

          <div className="flex-1" />

          {/* Log time button (available while running too) */}
          <button
            type="button"
            onClick={() => setShowLogInput(!showLogInput)}
            className="flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <PlusIcon className="size-3.5" />
            {t("timeTracking.log", "Log")}
          </button>

          {/* Stop button — same py-0.5 as Start to prevent height shift */}
          <button
            type="button"
            onClick={handleStop}
            className="flex items-center gap-1 rounded-md bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-500/20 dark:text-red-400"
          >
            <StopIcon className="size-3.5" />
            {t("timeTracking.stop", "Stop")}
          </button>
        </div>
      ) : (
        <div className="flex h-8 items-center gap-2">
          <TimerIcon className="size-4 text-muted-foreground" />

          {totalWithRunning > 0 && (
            <span className="text-sm font-semibold tabular-nums">
              {formatDuration(totalWithRunning)}
            </span>
          )}
          {entryCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-0.5 rounded px-1 py-0.5 text-xs tabular-nums text-muted-foreground/60 transition-colors hover:bg-accent hover:text-foreground"
            >
              <span>({entryCount})</span>
              <ChevronIcon className={`size-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
          )}

          <div className="flex-1" />

          {/* Start button */}
          <button
            type="button"
            onClick={handleStart}
            className="flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <PlayIcon className="size-3.5" />
            {t("timeTracking.start", "Start")}
          </button>

          {/* Log time button */}
          <button
            type="button"
            onClick={() => setShowLogInput(!showLogInput)}
            className="flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <PlusIcon className="size-3.5" />
            {t("timeTracking.log", "Log")}
          </button>
        </div>
      )}

      {/* Manual log input */}
      {showLogInput && (
        <div className="flex flex-col gap-2 rounded-lg border border-border p-2">
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              max={23}
              value={logHours}
              onChange={(e) => setLogHours(Math.max(0, Number(e.target.value)))}
              className="w-12 rounded-md border bg-transparent px-2 py-1 text-center text-xs"
            />
            <span className="text-xs text-muted-foreground">h</span>
            <input
              type="number"
              min={0}
              max={59}
              step={5}
              value={logMinutes}
              onChange={(e) => setLogMinutes(Math.max(0, Number(e.target.value)))}
              className="w-12 rounded-md border bg-transparent px-2 py-1 text-center text-xs"
            />
            <span className="text-xs text-muted-foreground">m</span>
            <button
              type="button"
              onClick={handleLogTime}
              className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground"
            >
              {t("timeTracking.addEntry", "Add")}
            </button>
          </div>
          <input
            type="text"
            value={logNote}
            onChange={(e) => setLogNote(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogTime()}
            placeholder={t("timeTracking.notePlaceholder", "Note (optional)")}
            className="rounded-md border bg-transparent px-2 py-1 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
      )}

      {/* Time entries — collapsed by default, shown on chevron click */}
      {expanded && entryCount > 0 && (
        <div className="flex flex-col gap-2 pt-1">
          {(() => {
            let remaining = EXPANDED_MAX
            return groupedEntries.map((group) => {
              if (remaining <= 0) return null
              const sliced = group.entries.slice(0, remaining)
              remaining -= sliced.length
              return (
                <TimeEntryGroup
                  key={group.dayKey}
                  label={group.label}
                  entries={sliced}
                  onDelete={handleDeleteEntry}
                />
              )
            })
          })()}

          {/* Show all link when more than EXPANDED_MAX */}
          {entryCount > EXPANDED_MAX && (
            <span className="self-start px-1.5 text-xs text-muted-foreground/60">
              {t("timeTracking.andMore", "+ {{count}} more", { count: entryCount - EXPANDED_MAX })}
            </span>
          )}
        </div>
      )}
    </div>
  )
})

// ---------------------------------------------------------------------------
// TimeEntryGroup — day group with header + entries with left accent border
// ---------------------------------------------------------------------------

const TimeEntryGroup = memo(function TimeEntryGroup({
  label,
  entries,
  onDelete,
}: {
  label: string
  entries: TimeEntry[]
  onDelete: (entryId: string) => void
}) {
  const dayTotal = entries.reduce((sum, e) => sum + e.durationMs, 0)

  if (entries.length === 0) return null

  return (
    <div className="flex flex-col">
      {/* Day header */}
      <div className="flex items-center gap-2 pb-1">
        <span className="text-xs font-medium text-muted-foreground/70">
          {label}
        </span>
        <span className="text-xs tabular-nums text-muted-foreground/40">
          {formatDuration(dayTotal)}
        </span>
      </div>

      {/* Entries with left accent border */}
      <div className="flex flex-col gap-0.5 border-l-2 border-border/50 pl-2.5">
        {entries.map((entry) => (
          <TimeEntryRow
            key={entry.id}
            entry={entry}
            onDelete={() => onDelete(entry.id)}
          />
        ))}
      </div>
    </div>
  )
})

// ---------------------------------------------------------------------------
// TimeEntry row — duration prominent, time range subtle, notes in quotes
// ---------------------------------------------------------------------------

/**
 * Format ms to compact duration: "1:23:45" or "45:00" (always shows minutes+seconds for precision)
 */
function formatEntryDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
  return `${m}:${String(s).padStart(2, "0")}`
}

const TimeEntryRow = memo(function TimeEntryRow({
  entry,
  onDelete,
}: {
  entry: TimeEntry
  onDelete: () => void
}) {
  const startTime = dayjs(Number(entry.startedAt)).format("HH:mm")
  const endTime = entry.endedAt ? dayjs(Number(entry.endedAt)).format("HH:mm") : "…"

  return (
    <div className="group flex items-center gap-2.5 py-0.5 text-xs">
      {/* Duration — primary info */}
      <span className="min-w-[3.5rem] tabular-nums font-medium text-foreground/80">
        {formatEntryDuration(entry.durationMs)}
      </span>

      {/* Time range or "manual" */}
      {entry.source === "timer" ? (
        <span className="tabular-nums text-muted-foreground/50">
          {startTime} – {endTime}
        </span>
      ) : (
        <span className="text-muted-foreground/40 italic">manual</span>
      )}

      {/* Note in quotes */}
      {entry.note && (
        <span className="max-w-[10rem] truncate text-muted-foreground/50">
          "{entry.note}"
        </span>
      )}

      {/* Delete — visible on hover */}
      <button
        type="button"
        onClick={onDelete}
        className="ml-auto flex items-center rounded p-0.5 opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-60"
        title="Delete entry"
      >
        <TrashIcon className="size-3" />
      </button>
    </div>
  )
})
